/**
 * The three boxed blocks: a note, a warning, a do/do-not pair and a message
 * template.
 *
 * Each is a `<div>` with a name the reader styles, and each holds ordinary
 * blocks inside it - paragraphs, lists, the odd `<h4>`. `content: "block+"`
 * rather than a fixed shape on purpose: a warning is sometimes one sentence and
 * sometimes a sentence plus a three-line list, and a schema that only allowed
 * the first would silently drop the list on the way in.
 *
 * `defining: true` matters more than it looks. Without it, selecting everything
 * inside a note and typing replaces the note itself rather than its contents,
 * so the box quietly disappears mid-edit.
 */
import { Node, mergeAttributes } from "@tiptap/core";

/** `<div class="kb-note">` and `<div class="kb-warn">` - one node, two tones. */
export const KbCallout = Node.create({
  name: "kbCallout",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      tone: {
        default: "note",
        parseHTML: (el) => (el.classList.contains("kb-warn") ? "warn" : "note"),
        renderHTML: (attrs) => ({ class: attrs.tone === "warn" ? "kb-warn" : "kb-note" }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.kb-note" }, { tag: "div.kb-warn" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes), 0];
  },
});

/** `<div class="kb-do">`: two headings and two lists, side by side for the reader. */
export const KbDo = Node.create({
  name: "kbDo",
  group: "block",
  content: "block+",
  defining: true,

  parseHTML() {
    return [{ tag: "div.kb-do" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "kb-do" }), 0];
  },
});

/** `<div class="kb-mail">`: a message the operator is meant to copy out. */
export const KbMail = Node.create({
  name: "kbMail",
  group: "block",
  content: "block+",
  defining: true,

  parseHTML() {
    return [{ tag: "div.kb-mail" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "kb-mail" }), 0];
  },
});
